import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useCallback, useContext, useEffect, useRef } from "react";
import {
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from "react-native";

import { getGetClinicStatusForPatientQueryKey } from "@/api/generated";
import { ClientEventModel } from "@/api/model";
import { useMarkConversationAsRead } from "@/api/mutations/conversation";
import { ScrollView, getTokenValue } from "@/components";
import ChatInput from "@/components/ChatInput";
import ImageGallery from "@/components/ImageGallery";
import { LayoutContext } from "@/components/LayoutProvider";
import ConversationClosedNote from "@/conversation/ConversationClosedNote";
import CuraiHeader from "@/conversation/CuraiHeader";
import CuraiMessageStream from "@/conversation/CuraiMessageStream";
import { useInfiniteMessages } from "@/hooks/useInfiniteMessages";
import { useIsSignedInStable } from "@/hooks/useIsSignedInStable";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import useRedirectUrl from "@/hooks/useRedirectUrl";
import { setTag } from "@/sentry";
import { useVideoStore } from "@/store";
import { getBrowserInfo } from "@/utils/web";
import StartVideoVisit from "./video/StartVideoVisit";

const AUTO_SCROLL_THRESHOLD = getTokenValue("$800");
const SCROLL_TO_LOAD_THRESHOLD = 100;
export default function CuraiConversation({
  conversationId,
}: {
  conversationId: string;
}) {
  const { browser, platform } = getBrowserInfo() ?? {};
  const router = usePropagatedRouter();
  const scrollViewRef = useRef<ScrollView>(null);
  const shouldAutoScrollRef = useRef(true);
  const isSignedInStable = useIsSignedInStable();
  const redirectUrl = useRedirectUrl();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error: getMessagesError,
  } = useInfiniteMessages(conversationId);

  useEffect(() => {
    if (getMessagesError && (getMessagesError as AxiosError).status === 404) {
      if (isSignedInStable) {
        router.navigate("/");
      } else {
        router.replace({ pathname: "/sign-in", params: { redirectUrl } });
      }
    }
  }, [getMessagesError, router, redirectUrl, isSignedInStable]);

  const { setShowHeader } = useContext(LayoutContext);

  const allMessages = data?.allMessages ?? [];
  const conversation = data?.pages[0]?.conversation;
  // A visit's own closed state
  const isConversationClosed = Boolean(conversation?.closed);
  const queryClient = useQueryClient();
  const shouldRefetchClinicStatus =
    conversation?.closed && conversation?.has_unread;

  const { mutate: markConversationAsRead } = useMarkConversationAsRead(
    conversation?.conversation_group_id,
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies(allMessages.length): used to trigger the effect
  useEffect(() => {
    if (conversation?.id) {
      markConversationAsRead(
        { conversationId: conversation.id },
        {
          onSuccess: () => {
            if (shouldRefetchClinicStatus) {
              queryClient.refetchQueries({
                queryKey: getGetClinicStatusForPatientQueryKey(),
                exact: false,
              });
            }
          },
        },
      );
    }
  }, [
    allMessages.length,
    conversation?.id,
    markConversationAsRead,
    queryClient,
    shouldRefetchClinicStatus,
  ]);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;

      if (
        contentOffset.y <= SCROLL_TO_LOAD_THRESHOLD &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }

      const distanceFromBottom =
        contentSize.height - (layoutMeasurement.height + contentOffset.y);
      shouldAutoScrollRef.current = distanceFromBottom <= AUTO_SCROLL_THRESHOLD;
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  useEffect(() => {
    setTag("conversation_id", conversationId);
  }, [conversationId]);

  useEffect(() => {
    setShowHeader(false);
    return () => {
      setShowHeader(true);
    };
  }, [setShowHeader]);

  const { isVideoVisitStarted } = useVideoStore();
  const mostRecentVideoVisitEvent = allMessages
    .filter(
      (message) =>
        message.type === "video_visit_started" ||
        message.type === "video_visit_ended",
    )
    .reduce(
      (mostRecent, current) => {
        if (!mostRecent) {
          return current;
        }
        return current?.createdAt > mostRecent?.createdAt
          ? current
          : mostRecent;
      },
      undefined as ClientEventModel | undefined,
    );

  const showStartVideoVisit =
    mostRecentVideoVisitEvent?.type === "video_visit_started" &&
    !isVideoVisitStarted &&
    !isConversationClosed;

  if (!conversation) return null;

  return (
    <>
      <CuraiHeader
        conversationTitle={conversation.title ?? "New conversation"}
        clinicianTitle={
          conversation.curai_clinician_full_name_with_title &&
          conversation.curai_clinician_full_name_with_title !== "System"
            ? conversation.curai_clinician_full_name_with_title
            : "Curai Health"
        }
      />
      <ScrollView
        flex={1}
        contentContainerStyle={{
          flexGrow: 1,
          flexShrink: 0,
          width: "100%",
          maxWidth: getTokenValue("$constraint"),
          alignSelf: "center",
          paddingTop: getTokenValue("$200"),
        }}
        paddingHorizontal="$200"
        $lg={{
          // 8px is reserved on lg devices for scrollbar gutter
          paddingHorizontal: "$100",
          ...(browser?.name === "Safari" &&
            platform?.type === "desktop" && {
              paddingLeft: "$400",
            }),
        }}
        style={{
          // @ts-expect-error ts(2353) 'scrollbarGutter' is a web only prop
          scrollbarGutter: "stable both-edges",
        }}
        ref={scrollViewRef}
        scrollEventThrottle={16}
        onScroll={handleScroll}
        onContentSizeChange={(e) => {
          if (!allMessages?.length) return;
          if (shouldAutoScrollRef.current) {
            scrollViewRef.current?.scrollToEnd({ animated: true });
          }
        }}
      >
        <CuraiMessageStream messages={allMessages} />
      </ScrollView>
      {showStartVideoVisit && (
        <StartVideoVisit curaiConversationId={conversationId} />
      )}
      {isConversationClosed ? (
        <ConversationClosedNote isCuraiConversation />
      ) : (
        <ChatInput
          constrained
          dataTrackingId="chat-input"
          extraTrackingContext={{ messageType: "curai" }}
          enableGradient={false}
          conversationType="curai"
        />
      )}
      <ImageGallery />
    </>
  );
}
